// Průvodce tvorbou Fokus seznamu — 5 kroků: zadání → AI návrhy → vlastní výběr → doladění → publikace.
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CircleNotch, Check, MagnifyingGlass, Plus, X, Trophy, ShareNetwork } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { createFocus, recruitFocus, focusRecommendations, findPlayers } from "../lib/lab-api";
import { focusListCreate, focusListUpdate } from "../lib/focus-api";
import { VerdictPicker } from "../components/lab/PublishFocusList";

const STEPS = ["Zadání", "AI návrhy", "Vlastní výběr", "Doladění", "Publikace"];
const POSITIONS = ["GKP", "DEF", "MID", "FWD"];
const RISKS = [["low", "Nízké"], ["medium", "Střední"], ["high", "Vysoké"]];
const SORTS = [["points", "Body"], ["hauls", "Hauly"], ["fdr_diff", "FDR"], ["consistency", "Konzistence"], ["value", "Value"], ["ownership", "Ownership"], ["preseason", "Preseason"]];
const inputCls = "bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] w-full focus:outline-none focus:border-fa-mint placeholder:text-white/25";
const chipCls = (on) => `font-mono text-[9px] uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${on ? "border-fa-mint bg-fa-mint/15 text-fa-mint" : "border-white/20 text-white/55 hover:border-white/50"}`;

function Progress({ step }) {
    return (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="wizard-progress">
            {STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-1.5">
                    <div className={`flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${i + 1 === step ? "border-fa-yellow text-fa-yellow" : i + 1 < step ? "border-fa-mint/50 text-fa-mint" : "border-white/10 text-white/30"}`}>
                        {i + 1 < step ? <Check size={10} weight="bold" /> : <span>{i + 1}</span>} {s}
                    </div>
                    {i < STEPS.length - 1 && <span className="text-white/15">—</span>}
                </div>
            ))}
        </div>
    );
}

const PlayerRow = ({ p, right }) => (
    <div className="flex items-center gap-2 border border-white/10 bg-black/40 px-3 py-2 flex-wrap">
        <span className="font-display font-bold text-sm">{p.name}</span>
        <span className="font-mono text-[9px] text-white/50">{p.team} · {p.position} · £{p.price}</span>
        {p.fit_score != null && <span className="font-mono text-[9px] text-fa-yellow">fit {Math.round(p.fit_score)}</span>}
        {p.total_points != null && <span className="font-mono text-[9px] text-white/45">{p.total_points} b</span>}
        <div className="ml-auto">{right}</div>
    </div>
);

export default function FocusWizard() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ title: "", position_filters: [], budget_max: "", gw_start: 1, gw_end: 4, risk_level: "medium", requirements: "" });
    const [focusId, setFocusId] = useState(null);
    const [recState, setRecState] = useState("idle"); // idle|running|ok|error
    const [recs, setRecs] = useState([]);
    const [aiSummary, setAiSummary] = useState(null);
    const [selected, setSelected] = useState({}); // fpl_id -> player
    const [q, setQ] = useState({ search: "", position: "", price_max: 16, sort: "points" });
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [listDoc, setListDoc] = useState(null);
    const [done, setDone] = useState(null);
    const pollRef = useRef(null);

    useEffect(() => () => clearInterval(pollRef.current), []);

    const toggleSel = (p) => setSelected((s) => {
        const next = { ...s };
        if (next[p.fpl_id]) delete next[p.fpl_id];
        else next[p.fpl_id] = p;
        return next;
    });

    // KROK 1 → 2: založí focus a spustí AI recruitera
    const submitCriteria = async () => {
        if (form.title.trim().length < 3) { toast.error("Napiš název (aspoň 3 znaky)"); return; }
        setBusy(true);
        try {
            const res = await createFocus({
                title: form.title.trim(), type: "free_form",
                position_filters: form.position_filters,
                budget_max: form.budget_max ? Number(form.budget_max) : null,
                gw_start: Number(form.gw_start) || 1, gw_end: Number(form.gw_end) || 4,
                risk_level: form.risk_level, requirements: form.requirements || null,
            });
            const f = res?.item || res;
            if (!f?.id) throw new Error("focus id missing");
            setFocusId(f.id);
            setStep(2);
            setRecState("running");
            await recruitFocus(f.id);
            pollRef.current = setInterval(async () => {
                try {
                    const d = await focusRecommendations(f.id);
                    if (d.recruit_status === "running") return;
                    clearInterval(pollRef.current);
                    if (d.recruit_status === "error") { setRecState("error"); toast.error(d.recruit_error || "AI recruiter selhal"); return; }
                    const items = (d.items || []).map((it) => ({ ...it, fpl_id: Number(it.fpl_id) }));
                    setRecs(items);
                    setAiSummary(d.ai_summary || null);
                    setSelected(Object.fromEntries(items.map((it) => [it.fpl_id, it])));
                    setRecState("ok");
                } catch { /* poll dál */ }
            }, 4000);
        } catch (e) { toast.error(e.response?.data?.detail || "Založení se nepovedlo"); }
        finally { setBusy(false); }
    };

    const search = async () => {
        setSearching(true);
        try {
            const d = await findPlayers({
                search: q.search || undefined, position: q.position || undefined,
                price_max: q.price_max, sort: q.sort, limit: 20,
            });
            setResults(d.results || d.rows || []);
        } catch { toast.error("Vyhledávání selhalo"); }
        finally { setSearching(false); }
    };

    // KROK 3 → 4: vytvoří draft seznam z výběru
    const buildList = async () => {
        const ids = Object.keys(selected).map(Number);
        if (!ids.length) { toast.error("Vyber aspoň jednoho hráče"); return; }
        setBusy(true);
        try {
            const doc = await focusListCreate(focusId, ids);
            setListDoc(doc);
            setStep(4);
        } catch (e) { toast.error(e.response?.data?.detail || "Vytvoření seznamu selhalo"); }
        finally { setBusy(false); }
    };

    const setPlayer = (fplId, patch) => setListDoc((d) => ({
        ...d, players: d.players.map((p) => (p.fpl_id === fplId ? { ...p, ...patch } : p)),
    }));

    const saveDraft = async (extra = {}) => {
        const d = await focusListUpdate(listDoc.id, {
            title: listDoc.title, intro: listDoc.intro,
            players: listDoc.players.map((p) => ({ fpl_id: p.fpl_id, note: p.note || null, included: p.included !== false, verdict: p.verdict || null })),
            ...extra,
        });
        setListDoc(d);
        return d;
    };

    const publish = async () => {
        setBusy(true);
        try {
            const d = await saveDraft({ status: "published" });
            setDone(d);
            toast.success("Publikováno!");
        } catch (e) { toast.error(e.response?.data?.detail || "Publikace selhala"); }
        finally { setBusy(false); }
    };

    const shareOnX = () => {
        const url = `${window.location.origin}/fokus/${done.slug}`;
        const text = `${done.title} — můj FPL Fokus seznam na Fantasy Academy. Souhlasíš s picky? Pojď hlasovat 👇`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank");
    };

    if (!user) {
        return (
            <AppShell>
                <div className="max-w-xl mx-auto text-center space-y-4 py-16" data-testid="wizard-login-cta">
                    <h1 className="font-display font-black uppercase text-2xl">Nový Fokus seznam</h1>
                    <p className="font-mono text-[11px] text-white/55">Pro tvorbu seznamů se přihlas — každý manažer může vytvořit až 3 seznamy týdně a získávat reputaci za trefené picky.</p>
                    <button onClick={login} className="font-mono text-[10px] uppercase tracking-widest px-6 py-2.5 bg-fa-mint text-black font-bold" data-testid="wizard-login-btn">Přihlásit se</button>
                </div>
            </AppShell>
        );
    }

    if (done) {
        return (
            <AppShell>
                <div className="max-w-xl mx-auto text-center space-y-5 py-14" data-testid="wizard-done">
                    <Trophy size={40} className="mx-auto text-fa-yellow" weight="fill" />
                    <h1 className="font-display font-black uppercase text-2xl">Seznam je venku!</h1>
                    <p className="font-mono text-[11px] text-white/55 leading-relaxed">Čtenáři teď můžou hlasovat o tvých picích. Po 3 gameweecích se seznam vyhodnotí — za každý trefený pick získáš <span className="text-fa-mint font-bold">+2 reputace</span>.</p>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                        <Link to={`/fokus/${done.slug}`} className="font-mono text-[10px] uppercase tracking-widest px-5 py-2.5 bg-fa-mint text-black font-bold" data-testid="wizard-view-list">Zobrazit seznam</Link>
                        <button onClick={shareOnX} className="font-mono text-[10px] uppercase tracking-widest px-5 py-2.5 border border-white/25 text-white/80 hover:border-white inline-flex items-center gap-1.5" data-testid="wizard-share-x"><ShareNetwork size={12} /> Sdílet na X</button>
                        <Link to="/fokus" className="font-mono text-[10px] uppercase tracking-widest px-5 py-2.5 border border-white/15 text-white/50 hover:text-white">Všechny seznamy</Link>
                    </div>
                </div>
            </AppShell>
        );
    }

    const selCount = Object.keys(selected).length;

    return (
        <AppShell>
            <div className="max-w-3xl mx-auto space-y-5" data-testid="focus-wizard-page">
                <Link to="/fokus" className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1"><ArrowLeft size={11} /> Fokus seznamy</Link>
                <h1 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl">Nový Fokus seznam</h1>
                <Progress step={step} />

                {step === 1 && (
                    <div className="space-y-3 border border-white/10 bg-[#0d0d0d] p-4 md:p-6" data-testid="wizard-step-1">
                        <p className="font-mono text-[10px] text-white/50">Popiš, co hledáš — AI Recruiter podle toho navrhne kandidáty.</p>
                        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Název (např. Levní obránci do £4.5 na GW1–6)" className={inputCls} data-testid="wizard-title-input" />
                        <div className="flex gap-1.5 flex-wrap">
                            {POSITIONS.map((p) => (
                                <button key={p} type="button" data-testid={`wizard-pos-${p}`}
                                        onClick={() => setForm({ ...form, position_filters: form.position_filters.includes(p) ? form.position_filters.filter((x) => x !== p) : [...form.position_filters, p] })}
                                        className={chipCls(form.position_filters.includes(p))}>{p}</button>
                            ))}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <label className="space-y-1"><span className="font-mono text-[8px] uppercase tracking-widest text-white/40">Max cena £</span>
                                <input type="number" step="0.5" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} placeholder="např. 6.0" className={inputCls} data-testid="wizard-budget-input" /></label>
                            <label className="space-y-1"><span className="font-mono text-[8px] uppercase tracking-widest text-white/40">GW od</span>
                                <input type="number" min="1" max="38" value={form.gw_start} onChange={(e) => setForm({ ...form, gw_start: e.target.value })} className={inputCls} data-testid="wizard-gw-start" /></label>
                            <label className="space-y-1"><span className="font-mono text-[8px] uppercase tracking-widest text-white/40">GW do</span>
                                <input type="number" min="1" max="38" value={form.gw_end} onChange={(e) => setForm({ ...form, gw_end: e.target.value })} className={inputCls} data-testid="wizard-gw-end" /></label>
                        </div>
                        <div className="flex gap-1.5">{RISKS.map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setForm({ ...form, risk_level: k })} className={chipCls(form.risk_level === k)} data-testid={`wizard-risk-${k}`}>{l} risk</button>
                        ))}</div>
                        <textarea value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} rows={2} placeholder="Poznámka pro AI i čtenáře (např. hledám diferenciály s dobrým rozpisem)…" className={inputCls} data-testid="wizard-requirements" />
                        <button onClick={submitCriteria} disabled={busy} className="font-mono text-[10px] uppercase tracking-widest px-5 py-2.5 bg-fa-mint text-black font-bold disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="wizard-step1-next">
                            {busy ? <CircleNotch size={12} className="animate-spin" /> : <ArrowRight size={12} />} Spustit AI návrhy
                        </button>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-3" data-testid="wizard-step-2">
                        {recState === "running" && (
                            <div className="border border-fa-sky/30 bg-fa-sky/5 p-6 text-center space-y-2" data-testid="wizard-recruiting">
                                <CircleNotch size={22} className="animate-spin mx-auto text-fa-sky" />
                                <p className="font-mono text-[10px] text-white/60">AI Recruiter prochází hráče podle tvého zadání… (~20–40 s)</p>
                            </div>
                        )}
                        {recState === "error" && (
                            <div className="border border-red-400/40 bg-red-400/5 p-4 space-y-2">
                                <p className="font-mono text-[10px] text-red-300">AI návrhy selhaly — můžeš pokračovat ručním výběrem, nebo to zkusit znovu.</p>
                                <button onClick={submitCriteria} className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-white/25 text-white/70">Zkusit znovu</button>
                            </div>
                        )}
                        {recState === "ok" && (
                            <>
                                {aiSummary && <p className="font-mono text-[10px] text-white/55 border-l-2 border-fa-sky pl-3 leading-relaxed">{aiSummary}</p>}
                                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">AI navrhla {recs.length} kandidátů — odškrtni, koho nechceš</p>
                                <div className="space-y-2">
                                    {recs.map((p) => (
                                        <PlayerRow key={p.fpl_id} p={p} right={
                                            <button type="button" onClick={() => toggleSel(p)} data-testid={`wizard-rec-toggle-${p.fpl_id}`}
                                                    className={chipCls(!!selected[p.fpl_id])}>{selected[p.fpl_id] ? "✓ V seznamu" : "Přidat"}</button>} />
                                    ))}
                                </div>
                            </>
                        )}
                        <div className="flex gap-2">
                            <button onClick={() => setStep(1)} className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-white/15 text-white/50 hover:text-white"><ArrowLeft size={11} className="inline mb-0.5" /> Zpět</button>
                            {recState !== "running" && (
                                <button onClick={() => setStep(3)} className="font-mono text-[10px] uppercase tracking-widest px-5 py-2 bg-fa-mint text-black font-bold" data-testid="wizard-step2-next">
                                    Pokračovat ({selCount}) <ArrowRight size={11} className="inline mb-0.5" />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-3" data-testid="wizard-step-3">
                        <p className="font-mono text-[10px] text-white/50">Doplň vlastní hráče — hledej jménem nebo použij naše filtry (hauly, FDR, konzistence…).</p>
                        <div className="grid md:grid-cols-4 gap-2">
                            <input value={q.search} onChange={(e) => setQ({ ...q, search: e.target.value })} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Jméno hráče…" className={inputCls} data-testid="wizard-search-input" />
                            <select value={q.position} onChange={(e) => setQ({ ...q, position: e.target.value })} className={inputCls} data-testid="wizard-search-pos">
                                <option value="">Všechny pozice</option>{POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                            <select value={q.sort} onChange={(e) => setQ({ ...q, sort: e.target.value })} className={inputCls} data-testid="wizard-search-sort">
                                {SORTS.map(([k, l]) => <option key={k} value={k}>Řadit: {l}</option>)}
                            </select>
                            <button onClick={search} disabled={searching} className="font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black disabled:opacity-40 inline-flex items-center justify-center gap-1.5" data-testid="wizard-search-btn">
                                {searching ? <CircleNotch size={12} className="animate-spin" /> : <MagnifyingGlass size={12} />} Hledat
                            </button>
                        </div>
                        {results.length > 0 && (
                            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                                {results.map((p) => (
                                    <PlayerRow key={p.fpl_id} p={p} right={
                                        <button type="button" onClick={() => toggleSel(p)} data-testid={`wizard-add-${p.fpl_id}`}
                                                className={chipCls(!!selected[p.fpl_id])}>{selected[p.fpl_id] ? <X size={11} /> : <Plus size={11} />}</button>} />
                                ))}
                            </div>
                        )}
                        <div className="border border-fa-yellow/25 bg-fa-yellow/5 p-3">
                            <p className="font-mono text-[9px] uppercase tracking-widest text-fa-yellow mb-1.5">Tvůj výběr ({selCount})</p>
                            <div className="flex gap-1.5 flex-wrap">
                                {Object.values(selected).map((p) => (
                                    <button key={p.fpl_id} type="button" onClick={() => toggleSel(p)} className="font-mono text-[9px] px-2 py-1 border border-white/20 text-white/75 hover:border-red-400 hover:text-red-300 inline-flex items-center gap-1" data-testid={`wizard-sel-${p.fpl_id}`}>
                                        {p.name} <X size={9} />
                                    </button>
                                ))}
                                {!selCount && <span className="font-mono text-[9px] text-white/35">Zatím prázdné</span>}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setStep(2)} className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-white/15 text-white/50 hover:text-white"><ArrowLeft size={11} className="inline mb-0.5" /> Zpět</button>
                            <button onClick={buildList} disabled={busy || !selCount} className="font-mono text-[10px] uppercase tracking-widest px-5 py-2 bg-fa-mint text-black font-bold disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="wizard-step3-next">
                                {busy ? <CircleNotch size={12} className="animate-spin" /> : <ArrowRight size={12} />} {busy ? "Připravuji data…" : `Sestavit seznam (${selCount})`}
                            </button>
                        </div>
                    </div>
                )}

                {step === 4 && listDoc && (
                    <div className="space-y-3" data-testid="wizard-step-4">
                        <p className="font-mono text-[10px] text-white/50">Dolaď titulek, úvod a ke každému hráči napiš, proč ho tam máš (čtenáři to uvidí).</p>
                        <input value={listDoc.title || ""} onChange={(e) => setListDoc({ ...listDoc, title: e.target.value })} className={`${inputCls} font-display font-bold text-base`} data-testid="wizard-list-title" />
                        <textarea value={listDoc.intro || ""} onChange={(e) => setListDoc({ ...listDoc, intro: e.target.value })} rows={2} placeholder="Úvod pro čtenáře…" className={inputCls} data-testid="wizard-list-intro" />
                        <div className="space-y-2">
                            {listDoc.players.map((p) => (
                                <div key={p.fpl_id} className="border border-white/15 bg-black/40 p-2.5 space-y-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-display font-bold text-sm">{p.name}</span>
                                        <span className="font-mono text-[9px] text-white/50">{p.team} · {p.position} · £{p.price}</span>
                                        <span className="ml-auto font-mono text-[9px] text-white/45">{p.predicted_total != null && <>model {p.predicted_total} b · </>}own {p.ownership_pct}%</span>
                                    </div>
                                    <VerdictPicker value={p.verdict} onChange={(v) => setPlayer(p.fpl_id, { verdict: v })} fplId={p.fpl_id} />
                                    <input value={p.note || ""} onChange={(e) => setPlayer(p.fpl_id, { note: e.target.value })} placeholder="Proč je v seznamu…" className={inputCls} data-testid={`wizard-note-${p.fpl_id}`} />
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setStep(3)} className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-white/15 text-white/50 hover:text-white"><ArrowLeft size={11} className="inline mb-0.5" /> Zpět</button>
                            <button onClick={async () => { setBusy(true); try { await saveDraft(); setStep(5); } catch { toast.error("Uložení selhalo"); } finally { setBusy(false); } }}
                                    disabled={busy} className="font-mono text-[10px] uppercase tracking-widest px-5 py-2 bg-fa-mint text-black font-bold disabled:opacity-40" data-testid="wizard-step4-next">
                                {busy ? "Ukládám…" : "Uložit a pokračovat"} <ArrowRight size={11} className="inline mb-0.5" />
                            </button>
                        </div>
                    </div>
                )}

                {step === 5 && listDoc && (
                    <div className="space-y-3" data-testid="wizard-step-5">
                        <div className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2">
                            <h2 className="font-display font-black uppercase text-lg">{listDoc.title}</h2>
                            {listDoc.intro && <p className="font-mono text-[10px] text-white/55 italic">{listDoc.intro}</p>}
                            <p className="font-mono text-[9px] text-white/45">{listDoc.players.filter((p) => p.included !== false).length} hráčů: {listDoc.players.filter((p) => p.included !== false).map((p) => p.name).join(", ")}</p>
                        </div>
                        <div className="border border-fa-mint/25 bg-fa-mint/5 p-4 space-y-1.5" data-testid="wizard-reputation-explainer">
                            <p className="font-mono text-[9px] uppercase tracking-widest text-fa-mint">Jak funguje reputace</p>
                            <p className="font-mono text-[10px] text-white/60 leading-relaxed">
                                Po publikaci mohou čtenáři u každého hráče hlasovat, jestli je to dobrý pick. Po 3 gameweecích se seznam automaticky vyhodnotí podle skutečných bodů a cen — za každý trefený pick získáš <span className="text-fa-mint font-bold">+2 reputace</span> a hlasující se správným názorem <span className="text-fa-mint font-bold">+5 reputace</span>. Tvoje jméno a reputace jsou u seznamu vidět.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setStep(4)} className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-white/15 text-white/50 hover:text-white"><ArrowLeft size={11} className="inline mb-0.5" /> Zpět</button>
                            <button onClick={publish} disabled={busy} className="font-mono text-[10px] uppercase tracking-widest px-6 py-2 bg-fa-yellow text-black font-bold disabled:opacity-40" data-testid="wizard-publish-btn">
                                {busy ? "Publikuji…" : "🚀 Publikovat na /fokus"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </AppShell>
    );
}
